/**
 * 내 문의 조회 및 관리 hook
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { questionsAPI } from "@/entities/support/api/questions-api";
import { showAlert } from "@/shared/lib/alert";

const MY_QUESTIONS_QUERY_KEY = ["myQuestions"];

interface UseMyQuestionsOptions {
  enabled?: boolean;
}

/**
 * 내 문의 목록 조회 hook
 */
export const useMyQuestions = (options?: UseMyQuestionsOptions) => {
  const { data, isLoading, error, isError, refetch } = useQuery({
    queryKey: MY_QUESTIONS_QUERY_KEY,
    queryFn: () => questionsAPI.getMyQuestions(),
    staleTime: 1000 * 60 * 5, // 5분
    gcTime: 1000 * 60 * 10, // 10분
    enabled: options?.enabled !== false,
  });

  return {
    questions: data || [],
    isLoading,
    error: error?.message || (isError ? "문의사항 조회 실패" : null),
    refetch,
  };
};

interface UpdateQuestionData {
  title: string;
  content: string;
  category: string;
}

/**
 * 문의 수정 hook
 */
export const useUpdateQuestion = (questionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateQuestionData) => {
      // API에서 updateQuestion 메서드가 있다고 가정
      return questionsAPI.updateQuestion?.(questionId, data);
    },
    onSuccess: () => {
      // 문의 목록 캐시 무효화 및 재조회
      queryClient.invalidateQueries({ queryKey: MY_QUESTIONS_QUERY_KEY });
      showAlert("문의가 수정되었습니다");
    },
    onError: (error: Error) => {
      showAlert(`수정 실패: ${error.message}`);
    },
  });
};
